import React, { memo, useEffect, useMemo, useState } from "react";
import { useTable, usePagination, useSortBy } from "react-table";

import styles from "./tableComponent.module.css";

const TableComponent = memo(
  ({
    columns,
    data,
    fetchData,
    onRowSelect,
    totalPages: controlledPageCount,
    totalElements,
    selectedPage,
    multiSelect = false,
    isPaginated = true,
    pageSize = 10,
    headerName,
    onButtonClick,
    clearSelectedRows,
  }) => {
    const [selectedRows, setSelectedRows] = useState([]);

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      page,
      canPreviousPage,
      canNextPage,
      pageOptions,
      gotoPage,
      nextPage,
      previousPage,
      state: { pageIndex },
    } = useTable(
      {
        columns,
        data,
        manualPagination: isPaginated,
        pageCount: isPaginated ? controlledPageCount : 1,
        initialState: { pageIndex: selectedPage, pageSize },
      },
      useSortBy,
      usePagination,
    );

    const handleRowClick = (row) => {
      if (multiSelect) {
        const selectedIndex = selectedRows.findIndex(
          (selectedRow) => selectedRow.id === row.original.id,
        );
        const newSelectedRows = [...selectedRows];
        if (selectedIndex === -1) {
          newSelectedRows.push(row.original);
        } else {
          newSelectedRows.splice(selectedIndex, 1);
        }
        setSelectedRows(newSelectedRows);
        onRowSelect(newSelectedRows);
      } else {
        setSelectedRows([row.original]);
        onRowSelect(row.original);
      }
    };

    const isSelected = (row) =>
      selectedRows.some((selected) => selected.id === row.original.id);

    useEffect(() => {
      if (isPaginated) {
        fetchData(pageIndex);
      }
    }, [pageIndex, isPaginated]);

    // useEffect(() => {
    //   if (totalElements) {
    //     fetchData(0);
    //     if (pageIndex !== 0) {
    //       gotoPage(0);
    //     }
    //   }
    //   setSelectedRows([]);
    // }, [totalElements]);

    useEffect(() => {
      if (pageIndex !== selectedPage) {
        gotoPage(selectedPage);
      }
    }, [selectedPage]);

    useEffect(() => {
      setSelectedRows([]);
    }, [clearSelectedRows]);

    return (
      <>
        <h2 className={styles["total-count"]}>
          {headerName ? headerName : `Результаты поиска: ${totalElements}`}
        </h2>
        <div className={styles["table-container"]}>
          <table {...getTableProps()} className={styles["table"]}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {multiSelect && <th></th>}
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps({
                        ...column.getSortByToggleProps(),
                        title: `Сортировать по ${column.render("Header")}`,
                      })}
                    >
                      {column.render("Header")}
                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? " ↓"
                            : " ↑"
                          : ""}
                      </span>
                    </th>
                  ))}
                  {onButtonClick && <th>Действия</th>}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    onClick={() => handleRowClick(row)}
                    className={`${isSelected(row) ? styles["selected"] : ""} ${
                      row.original.isDanger || row.original.isDanger
                        ? styles["isDanger"]
                        : ""
                    }`}
                  >
                    {multiSelect && (
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected(row)}
                          onChange={() => handleRowClick(row)}
                        />
                      </td>
                    )}
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                    {onButtonClick && (
                      <td className={styles["buttons-table"]}>
                        {row.original.isEditable && (
                          <div className={styles["delete-button-block"]}>
                            <span
                              onClick={() =>
                                onButtonClick("edit", row.original)
                              }
                              className={`${styles["edit-button"]} ${styles["delete-button"]}`}
                            ></span>
                          </div>
                        )}
                        {row.original.idDeleted && (
                          <div className={styles["delete-button-block"]}>
                            <span
                              onClick={() =>
                                onButtonClick("delete", row.original)
                              }
                              className={`${styles["delete-button"]} ${styles["delete-button"]}`}
                            ></span>
                          </div>
                        )}
                        {row.original.isTakeOff && (
                          <div className={styles["delete-button-block"]}>
                            <span
                              onClick={() =>
                                onButtonClick("takeOff", row.original)
                              }
                              className={`${styles["take-off-button"]}`}
                            >
                              Снять
                            </span>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {isPaginated && (
          <div className={styles["pagination"]}>
            <button
              className={styles["pagination-button"]}
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              {"<<"}
            </button>
            <button
              className={styles["pagination-button"]}
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              {"<"}
            </button>
            {pageOptions.map((page, i) =>
              i < pageIndex + 2 && i > pageIndex - 2 ? (
                <button
                  className={styles["pagination-button"]}
                  key={i}
                  onClick={() => gotoPage(i)}
                  style={{
                    fontWeight: pageIndex === i ? "bold" : "normal",
                    color: pageIndex === i ? "white" : "black",
                    backgroundColor: pageIndex === i ? "#BC8653" : "white",
                  }}
                >
                  {i + 1}
                </button>
              ) : null,
            )}
            <button
              className={styles["pagination-button"]}
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              {">"}
            </button>
            <button
              className={styles["pagination-button"]}
              onClick={() => gotoPage(controlledPageCount - 1)}
              disabled={!canNextPage}
            >
              {">>"}
            </button>
          </div>
        )}
      </>
    );
  },
);

export default TableComponent;
